import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageSquareText, RotateCcw, Save, Send, ShieldAlert, FlaskConical } from "lucide-react";
import {
    fetchTemplates, createTemplate, previewCampaign, createCampaign,
    fetchWaStatus, testSendWhatsApp, formatApiError,
} from "@/lib/api";

const inp = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-saffron";
const lab = "block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1";
const FLAG_LABEL = {
    missing_name: "Missing name", invalid_phone: "Invalid phone", missing_link: "No feedback link — regenerate it first",
    revoked: "Link revoked", already_submitted: "Feedback already submitted", already_invited: "Already invited",
    missing_coupon: "No coupon generated yet",
};

export function MessageGenerator({ token, eventId, events, onCampaignCreated }) {
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [message, setMessage] = useState("");
    const [preview, setPreview] = useState(null);
    const [selected, setSelected] = useState({});
    const [mode, setMode] = useState("manual");
    const [allowResend, setAllowResend] = useState(false);
    const [wa, setWa] = useState(null);
    const [testPhone, setTestPhone] = useState("");
    const [testSending, setTestSending] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [saveOpen, setSaveOpen] = useState(false);
    const [saveName, setSaveName] = useState("");
    const [saveLang, setSaveLang] = useState("en");
    const msgRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const [t, w] = await Promise.all([fetchTemplates(token), fetchWaStatus(token)]);
                setTemplates(t.templates);
                setWa(w);
                const def = t.templates.find((x) => x.is_default && x.language === "en") || t.templates[0];
                if (def && !message) { setTemplateId(def.id); setMessage(def.body); }
            } catch { toast.error("Could not load message templates"); }
        })();
        // eslint-disable-next-line
    }, [token]);

    // live per-recipient preview (debounced)
    useEffect(() => {
        if (!eventId || !message.trim()) { setPreview(null); return; }
        const t = setTimeout(async () => {
            try {
                const res = await previewCampaign(token, { event_id: eventId, message });
                setPreview(res);
                setSelected((prev) => {
                    const next = {};
                    for (const r of res.recipients) {
                        next[r.contact_id] = prev[r.contact_id] ?? (!r.hard_block && !r.flags.includes("already_invited"));
                    }
                    return next;
                });
            } catch { /* keep old preview on transient errors */ }
        }, 450);
        return () => clearTimeout(t);
    }, [token, eventId, message]);

    const insertPlaceholder = (p) => {
        const el = msgRef.current;
        if (!el) { setMessage((m) => m + p); return; }
        const s = el.selectionStart ?? message.length, e = el.selectionEnd ?? message.length;
        setMessage(message.slice(0, s) + p + message.slice(e));
        requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = s + p.length; });
    };

    const pickTemplate = (id) => {
        setTemplateId(id);
        const tpl = templates.find((t) => t.id === id);
        if (tpl) setMessage(tpl.body);
    };

    const resetTemplate = () => {
        const tpl = templates.find((t) => t.id === templateId) || templates.find((t) => t.is_default && t.language === "en");
        if (tpl) { setTemplateId(tpl.id); setMessage(tpl.body); toast.success("Reset to template"); }
    };

    const saveTemplate = async () => {
        if (!saveName.trim() || !message.trim()) { toast.error("Template name and message required"); return; }
        try {
            const tpl = await createTemplate(token, { name: saveName.trim(), language: saveLang, body: message });
            setTemplates((ts) => [...ts, tpl]);
            setTemplateId(tpl.id);
            setSaveOpen(false); setSaveName("");
            toast.success("Template saved — reusable for future events");
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const recipients = preview?.recipients || [];
    const chosenIds = recipients.filter((r) => selected[r.contact_id] && !r.hard_block).map((r) => r.contact_id);
    const firstSendable = recipients.find((r) => !r.hard_block);

    const doTestSend = async () => {
        if (!testPhone.trim()) { toast.error("Enter the admin-authorized test number"); return; }
        setTestSending(true);
        try {
            const res = await testSendWhatsApp(token, { phone: testPhone.trim(), message });
            toast.success(`Test accepted by Meta (id ${res.provider_message_id || "—"}). Delivery confirms via webhook.`);
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(d?.error === "whatsapp_not_configured" ? "WhatsApp API not configured — add the keys via Secrets panel" : formatApiError(d));
        } finally { setTestSending(false); }
    };

    const launch = async () => {
        setSending(true);
        try {
            const res = await createCampaign(token, {
                event_id: eventId, message, mode, contact_ids: chosenIds,
                allow_resend: allowResend, template_id: templateId || null,
            });
            toast.success(`Campaign created — ${res.messages_created} recipients queued${res.skipped_duplicates ? ` · ${res.skipped_duplicates} duplicates skipped` : ""}`);
            setConfirmOpen(false);
            onCampaignCreated?.(res.campaign.id);
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(d?.error === "whatsapp_not_configured" ? "WhatsApp API not configured yet" : formatApiError(d));
        } finally { setSending(false); }
    };

    return (
        <div className="mt-4" data-testid="message-generator">
            {/* template picker + editor */}
            <div className="grid lg:grid-cols-2 gap-5">
                <div>
                    <div className="flex flex-wrap items-end gap-2">
                        <div className="flex-1 min-w-[200px]">
                            <label className={lab}>Template</label>
                            <select className={inp} value={templateId} onChange={(e) => pickTemplate(e.target.value)} data-testid="mg-template-select">
                                {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.language === "hi" ? "Hindi" : "English"})</option>)}
                            </select>
                        </div>
                        <button onClick={resetTemplate} data-testid="mg-reset-template" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-stone-300 text-stone-500 text-[11px] font-black uppercase tracking-wider hover:border-navy hover:text-navy">
                            <RotateCcw className="h-3.5 w-3.5" /> Reset
                        </button>
                        <button onClick={() => setSaveOpen((v) => !v)} data-testid="mg-save-template-toggle" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-stone-300 text-stone-500 text-[11px] font-black uppercase tracking-wider hover:border-navy hover:text-navy">
                            <Save className="h-3.5 w-3.5" /> Save as template
                        </button>
                    </div>

                    {saveOpen && (
                        <div className="mt-2 flex flex-wrap items-end gap-2 rounded-xl bg-cream/60 border border-stone-200 p-3" data-testid="mg-save-template-form">
                            <div className="flex-1 min-w-[160px]"><label className={lab}>Template name</label>
                                <input className={inp} value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Monsoon picnic invite" data-testid="mg-save-template-name" /></div>
                            <div><label className={lab}>Language</label>
                                <select className={inp} value={saveLang} onChange={(e) => setSaveLang(e.target.value)} data-testid="mg-save-template-lang">
                                    <option value="en">English</option><option value="hi">Hindi</option>
                                </select></div>
                            <button onClick={saveTemplate} data-testid="mg-save-template-confirm" className="px-4 py-2 rounded-full bg-navy text-white text-[11px] font-black uppercase tracking-wider hover:bg-navy-light">Save</button>
                        </div>
                    )}

                    <div className="mt-3">
                        <div className="flex items-center justify-between">
                            <label className={lab}>Message</label>
                            <span className="text-[11px] font-bold text-stone-400" data-testid="mg-char-count">{message.length} chars</span>
                        </div>
                        <textarea ref={msgRef} rows={5} className={inp} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="mg-message-editor" />
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {["{{ChildName}}", "{{EventName}}", "{{FeedbackLink}}", "{{Code}}"].map((p) => (
                                <button key={p} onClick={() => insertPlaceholder(p)} data-testid={`mg-ph-${p.replace(/[{}]/g, "").toLowerCase()}`}
                                    className="rounded-full bg-gold/15 text-yellow-800 px-3 py-1 text-[11px] font-mono font-bold hover:bg-gold/30">{p}</button>
                            ))}
                        </div>
                    </div>

                    {/* live preview bubble */}
                    <div className="mt-3 rounded-xl bg-[#E7F6E9] border border-[#25D366]/30 p-4" data-testid="mg-live-preview">
                        <p className="text-[10px] font-black uppercase tracking-wider text-stone-500 mb-2">Live preview{firstSendable ? ` — ${firstSendable.name}` : ""}</p>
                        <div className="rounded-lg bg-white shadow-sm px-3 py-2 text-sm text-stone-800 whitespace-pre-wrap break-words max-w-md" data-testid="mg-preview-bubble">
                            {firstSendable ? firstSendable.message
                                : message
                                    ? message.replace("{{ChildName}}", "Aarav").replace("{{EventName}}", (events || []).find((e) => e.id === eventId)?.name || "the picnic").replace("{{FeedbackLink}}", "https://ghumnechaley.com/picnic-feedback?t=…").replace("{{Code}}", "GC-AB12CD34")
                                    : "Select an event with contacts to preview"}
                        </div>
                        {firstSendable && <p className="mt-1.5 text-[10px] text-stone-400">{firstSendable.chars} chars when sent</p>}
                    </div>
                </div>

                {/* recipients + send controls */}
                <div>
                    <div className="flex items-center justify-between">
                        <label className={lab}>Recipients{preview ? ` — ${chosenIds.length} of ${preview.summary.sendable} selected` : ""}</label>
                        {preview && (
                            <button onClick={() => {
                                const all = {};
                                for (const r of recipients) all[r.contact_id] = !r.hard_block && (allowResend || !r.flags.includes("already_invited"));
                                setSelected(all);
                            }} data-testid="mg-select-all" className="text-[11px] font-black uppercase tracking-wider text-navy hover:text-saffron">Select all eligible</button>
                        )}
                    </div>
                    <div className="rounded-xl border border-stone-200 max-h-72 overflow-y-auto" data-testid="mg-recipient-list">
                        {!preview ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-saffron" /></div> : (
                            recipients.length === 0 ? <p className="py-6 text-sm text-stone-500 text-center">No contacts for this event yet.</p> : (
                                <table className="w-full text-sm">
                                    <tbody>
                                        {recipients.map((r) => (
                                            <tr key={r.contact_id} className="border-b border-stone-100 last:border-0" data-testid={`mg-recipient-${r.contact_id}`}>
                                                <td className="py-2 pl-3 w-8">
                                                    <input type="checkbox" className="accent-saffron" disabled={r.hard_block}
                                                        checked={!!selected[r.contact_id] && !r.hard_block}
                                                        onChange={(e) => setSelected((s) => ({ ...s, [r.contact_id]: e.target.checked }))}
                                                        data-testid={`mg-select-${r.contact_id}`} />
                                                </td>
                                                <td className="py-2 pr-2">
                                                    <p className="font-bold text-navy leading-tight">{r.name || "(no name)"}</p>
                                                    <p className="text-xs text-stone-400">{r.phone || "—"}</p>
                                                </td>
                                                <td className="py-2 pr-3 text-right">
                                                    {r.flags.map((f) => (
                                                        <span key={f} className={`inline-block rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ml-1 ${f === "already_invited" ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-600"}`}
                                                            data-testid={`mg-flag-${r.contact_id}-${f}`}>{FLAG_LABEL[f] || f}</span>
                                                    ))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )
                        )}
                    </div>

                    {/* mode + test send */}
                    <div className="mt-4 rounded-xl border border-stone-200 p-4">
                        <label className={lab}>Send mode</label>
                        <div className="flex flex-col gap-2">
                            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="mg-mode-manual">
                                <input type="radio" className="mt-1 accent-saffron" checked={mode === "manual"} onChange={() => setMode("manual")} />
                                <span className="text-sm"><span className="font-bold text-navy">Assisted manual (wa.me)</span>
                                    <span className="block text-xs text-stone-500">Opens each chat on your WhatsApp (business phone) with the message pre-filled — you press send. Works today, no setup. Statuses are self-marked.</span></span>
                            </label>
                            <label className={`flex items-start gap-2.5 ${wa?.configured ? "cursor-pointer" : "opacity-60"}`} data-testid="mg-mode-api">
                                <input type="radio" className="mt-1 accent-saffron" disabled={!wa?.configured} checked={mode === "api"} onChange={() => setMode("api")} />
                                <span className="text-sm"><span className="font-bold text-navy">Automated — Meta WhatsApp Cloud API</span>
                                    {wa?.configured
                                        ? <span className="block text-xs text-green-700">Configured{wa.template_mode ? ` · template "${wa.template_name}"` : " · freeform (24h window only)"} · {wa.rate_per_sec}/sec</span>
                                        : <span className="block text-xs text-stone-500" data-testid="mg-api-missing">Not configured — missing: {(wa?.missing || []).join(", ")}. Add via Secrets panel to enable.</span>}
                                </span>
                            </label>
                        </div>

                        <label className="mt-3 flex items-center gap-2 text-xs font-bold text-stone-600 cursor-pointer">
                            <input type="checkbox" className="accent-saffron" checked={allowResend} onChange={(e) => setAllowResend(e.target.checked)} data-testid="mg-allow-resend" />
                            Allow resend to already-invited contacts (off = duplicates auto-skipped)
                        </label>

                        <div className="mt-3 flex items-end gap-2">
                            <div className="flex-1"><label className={lab}>Test send (API mode, admin number)</label>
                                <input className={inp} value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="99300 07445" data-testid="mg-test-phone" /></div>
                            <button onClick={doTestSend} disabled={testSending || !wa?.configured} data-testid="mg-test-send"
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-navy text-navy text-[11px] font-black uppercase tracking-wider hover:bg-navy hover:text-white disabled:opacity-50">
                                {testSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FlaskConical className="h-3.5 w-3.5" />} Test
                            </button>
                        </div>
                    </div>

                    <button onClick={() => { if (!chosenIds.length) { toast.error("Select at least one eligible recipient"); return; } setConfirmOpen(true); }}
                        disabled={!preview || sending || chosenIds.length === 0} data-testid="mg-review-send"
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-saffron text-white text-xs font-display font-black uppercase tracking-wider hover:bg-saffron-dark disabled:opacity-60">
                        <Send className="h-4 w-4" /> Review & send to {chosenIds.length} recipient{chosenIds.length === 1 ? "" : "s"}
                    </button>
                </div>
            </div>

            {/* confirmation modal */}
            {confirmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 p-4" data-testid="mg-confirm-modal">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                        <h3 className="font-display font-black text-lg text-navy flex items-center gap-2"><MessageSquareText className="h-5 w-5 text-saffron" /> Confirm campaign</h3>
                        <div className="mt-3 space-y-1.5 text-sm text-stone-600">
                            <p>Event: <span className="font-bold text-navy">{(events || []).find((e) => e.id === eventId)?.name}</span></p>
                            <p>Recipients: <span className="font-bold text-navy">{chosenIds.length}</span>
                                {preview?.summary.blocked ? <span className="text-red-600"> · {preview.summary.blocked} blocked (missing data)</span> : null}
                                {preview?.summary.already_invited && !allowResend ? <span className="text-blue-700"> · {preview.summary.already_invited} already invited (skipped)</span> : null}</p>
                            <p>Mode: <span className="font-bold text-navy">{mode === "manual" ? "Assisted manual (wa.me)" : "Meta Cloud API (automated queue)"}</span></p>
                        </div>
                        {mode === "manual" && (
                            <p className="mt-3 flex gap-2 rounded-lg bg-gold/15 p-3 text-xs text-yellow-900"><ShieldAlert className="h-4 w-4 shrink-0" /> Messages open in your WhatsApp one by one — you press send for each. Delivery is tracked by your confirmation, not by WhatsApp.</p>
                        )}
                        <div className="mt-5 flex gap-2 justify-end">
                            <button onClick={() => setConfirmOpen(false)} data-testid="mg-confirm-cancel" className="px-5 py-2.5 rounded-full border border-stone-300 text-stone-500 text-xs font-black uppercase tracking-wider hover:border-navy hover:text-navy">Cancel</button>
                            <button onClick={launch} disabled={sending} data-testid="mg-confirm-send" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-saffron text-white text-xs font-display font-black uppercase tracking-wider hover:bg-saffron-dark disabled:opacity-60">
                                {sending && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create campaign
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
